import { service } from '@ember/service';
import Controller from '@ember/controller';
import { task, all } from 'ember-concurrency';
import { isEmpty } from '@ember/utils';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { waitFor } from '@ember/test-waiters';
import { PERMISSION_NAMES } from 'eflex/constants/user-permissions';
import { prop, last, pipe, propOr } from 'ramda';
import { sortByProp } from 'ramda-adjunct';

export default class ContainPartsController extends Controller {
  @service store;
  @service intl;
  @service router;
  @service currentUser;
  @service jemRepo;

  queryParams = ['serialNumber', 'campaign'];

  @tracked serialNumber;
  @tracked campaign;
  @tracked buildStatuses = [];
  jemCampaigns = this.store.peekAll('jem-campaign');
  jemContainments = this.store.peekAll('jem-containment');
  buildStatus = this.store.peekAll('build-status');
  permission = PERMISSION_NAMES.containment;

  get hasPermission() {
    return this.currentUser.user.hasPermission(this.permission);
  }

  get dirtyCampaigns() {
    return this.jemCampaigns?.filter(item => item.isDirty) ?? [];
  }

  get dirtyContainments() {
    return this.jemContainments?.filter(item => item.isDirty) ?? [];
  }

  get displayedContainments() {
    if (this.campaign) {
      return this.jemContainments
        ?.filter(item => !item.scrap)
        ?.filter(item => !item.isDeleted)
        .filter(item => item.campaign === this.campaign) ?? [];
    } else {
      return this.jemContainments
        ?.filter(item => !item.scrap)
        ?.filter(item => !item.isDeleted) ?? [];
    }
  }

  get isDirty() {
    return !isEmpty(this.dirtyCampaigns) || !isEmpty(this.dirtyContainments);
  }

  get nonDeletedJemContainments() {
    return this.jemContainments.filter(item => !item.isDeleted);
  }

  get isInvalid() {
    return this.nonDeletedJemContainments.some(item => item.hasError || item.isInvalid);
  }

  updateBuildStatusAfterUncontain(serialNumber) {
    const buildStatus = this.store.peekAll('build-status').find(item => item.serialNumber === serialNumber);

    const lastStatus = pipe(
      prop('children'),
      sortByProp('timestamp'),
      last,
      propOr(null, 'status'),
    );

    if (buildStatus) {
      buildStatus.status = lastStatus(buildStatus);

      buildStatus.save();

      this.buildStatus = this.buildStatus.map(data =>
        (data.serialNumber === serialNumber ? buildStatus : data),
      );
    }
  }

  save = task(waitFor(async () => {
    if (this.isInvalid) {
      return;
    }

    await all(this.dirtyCampaigns.map(dirtyCampaign => dirtyCampaign.save()));
    await all(this.dirtyContainments.map(dirtyContainment => dirtyContainment.save()));
  }));

  @action
  campaignAddText(term) {
    return `${this.intl.t('add')} ${term}`;
  }

  @action
  showCreateOption(term, jemCampaigns) {
    return !jemCampaigns.some(item => item.name === term);
  }

  @action
  updateCampaign(campaign) {
    const name = campaign.isDefaultOption != null ? null : campaign.name;
    this.campaign = name;
  }

  @action
  createCampaign(containment, name) {
    const campaign = this.store.createRecord('jem-campaign', { name });
    this.addCampaign(containment, campaign);
  }

  @action
  addCampaign(containment, { name }) {
    containment.campaign = name;
  }

  @action
  addContainment() {
    this.store.createRecord('jem-containment', {
      userName: this.currentUser.user.userName,
      campaign: this.campaign,
    });
  }

  @action
  rollback() {
    this.jemContainments.forEach(jemContainment => { jemContainment.rollbackAttributes(); });
    this.jemCampaigns.forEach(jemCampaign => { jemCampaign.rollbackAttributes(); });
  }

  @action
  uncontain(containment) {
    this.updateBuildStatusAfterUncontain(containment.serialNumber);
    containment.deleteRecord();
  }

  @action
  searchSerial() {
    this.store.unloadAll('build-status');
    this.store.unloadAll('jem-containment');
    this.router.refresh('containParts');
  }
}
